// @ts-nocheck

import { ChangeEvent, ReactNode, useCallback, useState } from "react";
import Skeleton from "react-loading-skeleton";
import cn from "classnames";

import styles from "./Table.module.scss";
import Columns from "./Columns";
import Rows from "./Rows";
import Checkbox from "../BulkActions/Checkbox";
import { QueryOptions, TableProps } from "../Table.props";
import TableWrapper from "../TableWrapper";
import { Form } from "../../Form";

/**
 * @deprecated Use `import { Table } from capitalrx-components-2.0/lib/general` instead.
 */
function Table<T>(props: TableProps<T>): JSX.Element {
  const initialQueryOption = {
    results_per_page: 10,
    page_number: 1,
    order_by: [] as QueryOptions["order_by"],
  };

  const {
    data,
    headers,
    queryOptions = initialQueryOption,
    handleUpdateRequest,
    bulkActions,
    testId,
    handleRowClick,
    paginate = true,
    isLoading,
    className,
    paginatePosition = "all",
  } = props;

  const resultsTotalCount = parseInt(String(props.resultsTotalCount));
  // query_modifier can send back maximum 1000 items
  const resultsMoreFlag = resultsTotalCount === 1000;

  const [selectAllChecked, setSelectAllChecked] = useState(false);

  // holds an array indicating the index of the rows that are checked (checked === true)
  const [checkedRows, setCheckedRows] = useState<boolean[]>(() => {
    if (queryOptions) {
      return new Array(queryOptions.results_per_page).fill(false);
    } else {
      return [];
    }
  });

  const resetCheckedRows = useCallback(() => {
    setSelectAllChecked(false);
    setCheckedRows(Array(queryOptions.results_per_page).fill(false));
  }, []);

  const createTableLoadingFiller = useCallback(() => {
    const tableFillerRow = headers.reduce((row, { key }) => {
      return { ...row, [key as string]: <Skeleton height={20} width={100} /> };
    }, {});
    return [...Array(queryOptions.results_per_page).fill(tableFillerRow)];
  }, [isLoading, queryOptions]);

  const handleBulkActionsApply = (selectedFunction: string) => {
    if (bulkActions && checkedRows) {
      const checkedRowsData = [];
      for (let i = 0; i <= data.length; ++i) {
        if (checkedRows[i]) {
          checkedRowsData.push(data[i]);
        }
      }

      if (typeof bulkActions[selectedFunction] === "function") {
        bulkActions[selectedFunction](checkedRowsData);
      }
    }
  };

  const checkboxChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>, itemIndex: number) => {
      setCheckedRows((prev) => {
        const tmp = [...prev];
        tmp[itemIndex] = e.target.checked;

        for (let i = 0; i < data.length; i++) {
          tmp[itemIndex] = !!tmp[itemIndex];
        }

        if (tmp.every((v) => v === true)) {
          setSelectAllChecked(true);
        } else {
          setSelectAllChecked(false);
        }

        return [...tmp];
      });
    },
    [checkedRows],
  );

  let displayData = data;
  if (isLoading && data.length === 0) {
    displayData = createTableLoadingFiller();
  }

  const newHeaderData = [...headers];
  if (typeof bulkActions !== "undefined") {
    newHeaderData.unshift({
      key: "checkbox-select-all",
      label: (
        <span
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Checkbox
            name="checkbox-select-all"
            value="CheckedBox"
            id="checkbox-select-all"
            selected={selectAllChecked}
            handleOnChange={(e: ChangeEvent<HTMLInputElement>) => {
              setSelectAllChecked(e.target.checked);
              setCheckedRows(() => new Array(data.length).fill(e.target.checked));
            }}
          />
        </span>
      ),
      headerOnClick: undefined,
    });
  }

  /*
    Here we build the rows that contain the data into an array named formattedData[].
    We build dataObj{}, which represents a row.
    Its keys are the matching key from the header.
    Its values could be nearly anything (string, another component, -, etc...).
  */

  const formattedData: Record<string | number, string | number | ReactNode | null>[] = [];

  displayData?.forEach((item, itemIndex): void => {
    const dataObj: Record<string | number, string | number | ReactNode | null> = {};

    newHeaderData.forEach((header) => {
      const { key, cell, formatter } = header;
      if (key !== undefined) {
        const keyValue = key;
        const itemValue = item[keyValue as keyof object];

        if (key === "checkbox-select-all" && checkedRows) {
          // the header has had a checkbox injected into it, so now this row needs a checkbox
          dataObj[key] = (
            <span
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <Checkbox
                containerClassName="data-checkbox"
                name={`checkbox-${queryOptions?.page_number || 0}-${itemIndex}`}
                id={`checkbox-${queryOptions?.page_number || 0}-${itemIndex}`}
                selected={!!checkedRows[itemIndex]}
                handleOnChange={(e: ChangeEvent<HTMLInputElement>) => {
                  checkboxChange(e, itemIndex);
                }}
              />
            </span>
          );
        } else if (cell) {
          // if custom cell will set the key to the custom cell object
          dataObj[keyValue] = cell(item);
        } else if (itemValue === null || itemValue === undefined) {
          // if data is null will set data to empty cell
          dataObj[keyValue] = "-";
        } else if (formatter) {
          // will format label data ie. dates/ time / put in a tag or other component/ etc
          dataObj[keyValue] = formatter(itemValue);
        } else {
          // otherwise set data equal to its key
          dataObj[keyValue] = itemValue;
        }
      }
    });

    formattedData.push(dataObj);
  });

  const tableClass = cn(styles["table-main"], className);

  let TableComponent = (
    <div className={cn({ "table-wrapper": true, "has-data-load-indicators": true, "data-loading": isLoading, "data-loaded": !isLoading })}>
      <TableWrapper
        resultsTotalCount={resultsTotalCount}
        queryOptions={queryOptions}
        handleUpdateRequest={handleUpdateRequest}
        bulkActions={bulkActions}
        handleBulkActionsApply={handleBulkActionsApply}
        paginate={paginate}
        resetTable={resetCheckedRows}
        testId={testId}
        paginatePosition={paginatePosition}
        resultsMoreFlag={resultsMoreFlag}
      >
        <table className={tableClass} data-testid={testId ? `test__${testId}` : undefined}>
          <thead>
            <Columns
              headers={newHeaderData}
              queryOptions={queryOptions}
              handleUpdateRequest={handleUpdateRequest}
              resetTable={resetCheckedRows}
              testId={testId}
            />
          </thead>
          <tbody>
            <Rows formattedData={formattedData} data={data} resetTable={resetCheckedRows} handleRowClick={handleRowClick} testId={testId} />
          </tbody>
        </table>
      </TableWrapper>
    </div>
  );

  if (bulkActions) {
    TableComponent = <Form onSubmit={() => null}>{TableComponent}</Form>;
  }

  return TableComponent;
}

/**
 * @deprecated
 */
export default Table;
